// hooks/useExecutionPlan.js
import { useState, useEffect } from "react"
import { useAuth0 } from '@auth0/auth0-react';
import { getApiBaseUrl } from "../config/apiBase"

const useExecutionPlan = (actionId) => {
  const [executionPlan, setExecutionPlan] = useState({
    overview: "",
    steps: [],
    risks: "",
    successCriteria: "",
  })
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState({
    overview: "",
    steps: "",
    risks: "",
    successCriteria: "",
  })
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)
   const { getAccessTokenSilently } = useAuth0();

  const apiUrl = getApiBaseUrl()

  const fetchExecutionPlanData = async () => {
    try {
      setLoading(true)
      const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_API_AUDIENCE,
                }
            });

      // Fetch the current execution plan
      const planResponse = await fetch(`${apiUrl}/api/actions/${actionId}/refinement/modules/executionPlan`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (planResponse.ok) {
        const planData = await planResponse.json()
        setExecutionPlan(
          planData || {
            overview: "",
            steps: [],
            risks: "",
            successCriteria: "",
          },
        )
      }

      // Fetch suggestions
      const suggestionsResponse = await fetch(`${apiUrl}/api/actions/${actionId}/refinement/modules/executionPlan/suggestions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        setSuggestions(suggestionsData || [])
      }

      // Fetch comments
      const commentsResponse = await fetch(`${apiUrl}/api/actions/${actionId}/refinement/modules/executionPlan/comments`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        setComments(commentsData || [])
      }
    } catch (error) {
      console.error("Error fetching execution plan data:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitSuggestion = async () => {
    if (!newSuggestion.overview) return

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/actions/${actionId}/refinement/modules/executionPlan/suggestions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: JSON.stringify(newSuggestion) }),
      })

      if (response.ok) {
        setNewSuggestion({
          overview: "",
          steps: "",
          risks: "",
          successCriteria: "",
        })
        fetchExecutionPlanData()
      } else {
        console.error("Failed to submit suggestion")
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(`${apiUrl}/api/actions/${actionId}/refinement/modules/executionPlan/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      if (response.ok) {
        setNewComment("")
        fetchExecutionPlanData()
      } else {
        console.error("Failed to submit comment")
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })

      const response = await fetch(
        `${apiUrl}/api/actions/${actionId}/refinement/suggestions/${suggestionId}/vote`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ isUpvote: voteType === "up" }),
        },
      )

      if (response.ok) {
        fetchExecutionPlanData()
      } else {
        console.error("Failed to submit vote")
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  const updateNewSuggestion = (field, value) => {
    setNewSuggestion(prev => ({ ...prev, [field]: value }))
  }

  useEffect(() => {
    if (actionId) {
      fetchExecutionPlanData()
    }
  }, [actionId])

  return {
    // State
    executionPlan,
    suggestions,
    comments,
    newSuggestion,
    newComment,
    activeTab,
    loading,
    
    // Actions
    setActiveTab,
    setNewComment,
    updateNewSuggestion,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    fetchExecutionPlanData
  }
}

export default useExecutionPlan
